import {type FC, useCallback, useEffect, useMemo, useRef} from 'react';
import {InView} from 'react-intersection-observer';
import {useNavigate} from 'react-router';

import {Observer} from 'mobx-react';

import type {CoreUserEventsCountParams} from '@yourcoach/shared/api/types/core/methods';
import type {GetComponentProps} from '@yourcoach/shared/utils/utility-types';

import {delay} from '@yourcoach/async-utils';
import {useI18n} from '@yourcoach/i18n';
import {eventStore} from '@yourcoach/shared/api/event';
import {apiRequest} from '@yourcoach/shared/api/utils/apiRequest';
import AlarmIcon from '@yourcoach/shared/assets/icons/alarm.svg';
import {NotificationsListItem} from '@yourcoach/shared/modules/notifications/components/NotificationsListItem';
import {NotificationsStore} from '@yourcoach/shared/modules/notifications/stores/Notifications';
import {getExcludedEvents} from '@yourcoach/shared/modules/notifications/stores/Notifications/utils';
import {LoadingSpinner} from '@yourcoach/shared/uikit/LoadingSpinner';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '@yourcoach/shared/utils/eventEmitter';
import {logger} from '@yourcoach/shared/utils/logger';
import {useMobxStore} from '@yourcoach/shared/utils/store';

import NoResultsHeader from '@src/components/NoResultsHeader';
import localAppStore from '@src/context/appStore';

import type {Props} from './types';

import {getClickHandler} from './getClickHandler';
import * as S from './styles';

export const NotificationsList: FC<Props> = () => {
  const {t} = useI18n();
  const store = useMobxStore(() => new NotificationsStore());

  const fetchUnreadEventsCountDelay = useRef(delay(0));
  const readAllEventsDelay = useRef(delay(0));
  const isPaginating = useRef(false);
  const inViewRef = useRef(false);

  const navigate = useNavigate();

  const excludedEvents = useMemo(() => getExcludedEvents(), []);

  useEffect(() => {
    readAllEvents();
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUnreadEventsCount = useCallback(async () => {
    try {
      const result = await apiRequest<CoreUserEventsCountParams>({
        method: 'user.events.count',
        params: {
          query: [
            ['type', '!in', excludedEvents],
            ['read', '==', null],
          ],
        },
      });

      localAppStore.setTopNotification(result._count);

      return result._count;
    } catch (error) {
      logger.error(error);

      fetchUnreadEventsCountDelay.current = delay(2000);

      await fetchUnreadEventsCountDelay.current;

      return fetchUnreadEventsCount();
    }
  }, [excludedEvents]);

  const readAllEvents = useCallback(async () => {
    try {
      await eventStore.markReadAll({
        limit: 1000,
        query: [['type', '!in', excludedEvents]],
      });

      localAppStore.setTopNotification(0);

      return true;
    } catch (error) {
      logger.error(error);

      readAllEventsDelay.current = delay(2000);

      await readAllEventsDelay.current;

      return readAllEvents();
    }
  }, [excludedEvents]);

  const refresh = useCallback(
    async (silent = false) => {
      store.fetch(silent);

      fetchUnreadEventsCount();
    },
    [fetchUnreadEventsCount, store],
  );

  useEffect(() => {
    emitter.on(WS_RECEIVE_MESSAGE_EVENT, _handleWsMessage);

    return () => {
      emitter.off(WS_RECEIVE_MESSAGE_EVENT, _handleWsMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _handleWsMessage = msg => {
    if (!excludedEvents.includes(msg.event.type)) {
      refresh(true);
    }
  };

  const handleOnScrollLastElement = async (inView: boolean) => {
    inViewRef.current = inView;

    if (inView) {
      await fetchNext();

      if (inViewRef.current) {
        await fetchNext();
      }
    }
  };

  const fetchNext = async () => {
    if (isPaginating.current) {
      return;
    }

    isPaginating.current = true;

    await store.fetchMore();

    isPaginating.current = false;
  };

  const onClick: GetComponentProps<typeof NotificationsListItem>['onClick'] =
    useCallback(
      (id, event, contexts) => {
        const handler = getClickHandler({contexts, event, id, navigate, store});

        if (handler) {
          handler();

          localAppStore.setIsTopNotificationsOpen(false);
        }
      },
      [navigate, store],
    );

  return (
    <Observer>
      {() => (
        <S.Container>
          {store.isFetching ? (
            <S.LoadingSpinnerContainer>
              <LoadingSpinner size={100} />
            </S.LoadingSpinnerContainer>
          ) : store.isEmpty ? (
            <S.NoResultsContainer>
              <NoResultsHeader
                icon={AlarmIcon}
                text={t('web-app:Notifications.no_results_label')}
              />
            </S.NoResultsContainer>
          ) : (
            <div>
              {store.sections.map((section, index) => {
                return (
                  <div key={index}>
                    <S.SectionHeader>
                      <S.SectionTitle>{section.title}</S.SectionTitle>
                      {section.badge ? (
                        <S.SectionBadge>{section.badge}</S.SectionBadge>
                      ) : null}
                    </S.SectionHeader>
                    <S.SectionContent>
                      {section.data.map(event => (
                        <NotificationsListItem
                          event={event}
                          key={event._id}
                          onClick={onClick}
                        />
                      ))}
                    </S.SectionContent>
                  </div>
                );
              })}
              <InView onChange={handleOnScrollLastElement}>
                <Observer>
                  {() => (
                    <>
                      {store.itemsCount > 0 &&
                      store.earlierEventsStore.cursor.next ? (
                        <S.LoadingSpinnerContainer>
                          <LoadingSpinner size={50} />
                        </S.LoadingSpinnerContainer>
                      ) : null}
                    </>
                  )}
                </Observer>
              </InView>
            </div>
          )}
        </S.Container>
      )}
    </Observer>
  );
};
